import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Plus, FolderOpen, Users, Settings, FileText, Activity } from 'lucide-react';
import { useAppSelector } from '../store';
import { hasPermission as hasOldPermission } from '../lib/permissions';
import { hasPermission as hasGranularPermission } from '../config/permissions';
import { cn } from '../lib/utils';
import CreateEventModal from '../components/command/CreateEventModal';
import ViewEventsModal from '../components/command/ViewEventsModal';
import UserDropdown from '../components/shared/UserDropdown';
import EmergencyAlert from '../components/shared/EmergencyAlert';
import type { C2Event } from '../types';

export default function CommandHomePage() {
  const navigate = useNavigate();
  const { user } = useAppSelector((state) => state.auth);
  const [showCreate, setShowCreate] = useState(false);
  const [showEvents, setShowEvents] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());

  // Clock
  useState(() => {
    const t = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(t);
  });

  const canCreate = hasOldPermission(user?.role, 'create_incidents');
  const canManageUsers = hasOldPermission(user?.role, 'manage_users');
  const canAdmin = hasOldPermission(user?.role, 'manage_system');
  const canViewPRFs = user && hasGranularPermission(user.role, user.customPermissions, 'prfs:view');

  const handleEventCreated = (event: C2Event) => {
    // After creating, navigate into the event workspace
    navigate(`/workspace/incidents?eventId=${event.id}`);
  };

  const handleEventSelected = (event: C2Event) => {
    setShowEvents(false);
    navigate(`/workspace/incidents?eventId=${event.id}`);
  };

  const actions = [
    {
      id: 'create',
      label: 'Create New Event',
      description: 'Set up a new operational event with resources, maps, and documents',
      icon: Plus,
      color: 'text-emerald-400',
      bgColor: 'bg-emerald-500/10 border-emerald-500/20 hover:bg-emerald-500/15 hover:border-emerald-500/30',
      glowColor: '#22c55e',
      enabled: canCreate,
      onClick: () => setShowCreate(true),
    },
    {
      id: 'view',
      label: 'View Open Events',
      description: 'Browse active, supervised, and closed events',
      icon: FolderOpen,
      color: 'text-blue-400',
      bgColor: 'bg-blue-500/10 border-blue-500/20 hover:bg-blue-500/15 hover:border-blue-500/30',
      glowColor: '#3b82f6',
      enabled: true,
      onClick: () => setShowEvents(true),
    },
    {
      id: 'prfs',
      label: 'Patient Report Forms',
      description: 'View and search all submitted Patient Report Forms',
      icon: FileText,
      color: 'text-rose-400',
      bgColor: 'bg-rose-500/10 border-rose-500/20 hover:bg-rose-500/15 hover:border-rose-500/30',
      glowColor: '#f43f5e',
      enabled: canViewPRFs,
      onClick: () => navigate('/prfs'),
    },
    {
      id: 'users',
      label: 'Manage Users',
      description: 'Create, edit, and manage user accounts and permissions',
      icon: Users,
      color: 'text-amber-400',
      bgColor: 'bg-amber-500/10 border-amber-500/20 hover:bg-amber-500/15 hover:border-amber-500/30',
      glowColor: '#f59e0b',
      enabled: canManageUsers,
      onClick: () => navigate('/admin?tab=users'),
    },
    {
      id: 'admin',
      label: 'System Admin',
      description: 'Roles, permissions, and system configuration',
      icon: Settings,
      color: 'text-purple-400',
      bgColor: 'bg-purple-500/10 border-purple-500/20 hover:bg-purple-500/15 hover:border-purple-500/30',
      glowColor: '#a855f7',
      enabled: canAdmin,
      onClick: () => navigate('/admin?tab=roles'),
    },
    {
      id: 'audit',
      label: 'Audit Logs',
      description: 'View system activity and audit logs',
      icon: Activity,
      color: 'text-cyan-400',
      bgColor: 'bg-cyan-500/10 border-cyan-500/20 hover:bg-cyan-500/15 hover:border-cyan-500/30',
      glowColor: '#06b6d4',
      enabled: canAdmin,
      onClick: () => navigate('/admin?tab=logs'),
    },
  ];

  return (
    <div className="min-h-screen bg-[#0a0e1a] flex flex-col">
      {/* Ambient background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-[-20%] left-[-10%] w-[60%] h-[60%] rounded-full opacity-[0.03]"
          style={{ background: 'radial-gradient(circle, #3b82f6, transparent 70%)' }} />
        <div className="absolute bottom-[-20%] right-[-10%] w-[50%] h-[50%] rounded-full opacity-[0.03]"
          style={{ background: 'radial-gradient(circle, #6366f1, transparent 70%)' }} />
      </div>

      {/* Header */}
      <header className="relative z-10 flex items-center justify-between px-8 py-4 border-b"
        style={{ borderColor: 'rgba(255,255,255,0.06)', background: 'rgba(15,20,35,0.6)', backdropFilter: 'blur(12px)' }}
      >
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-primary flex items-center justify-center">
            <Shield className="w-5 h-5 text-primary-foreground" />
          </div>
          <div>
            <h1 className="text-sm font-bold text-white">C2 Command System</h1>
            <p className="text-[10px] text-white/30 uppercase tracking-widest">Control Room</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <p className="text-sm font-mono font-bold text-white/80">
              {currentTime.toLocaleTimeString('en-US', { hour12: false })}
            </p>
            <p className="text-[10px] text-white/30">
              {currentTime.toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
            </p>
          </div>
          <div className="w-px h-8 bg-white/10" />
          <UserDropdown />
        </div>
      </header>

      {/* Main Content */}
      <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-8 py-12">
        <div className="text-center mb-10">
          <h2 className="text-2xl font-bold text-white mb-2">Command Centre</h2>
          <p className="text-sm text-white/40">Select an action to begin</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 max-w-3xl w-full">
          {actions.map((action) => (
            <button
              key={action.id}
              onClick={action.onClick}
              disabled={!action.enabled}
              className={cn(
                'group relative flex flex-col items-start gap-4 p-6 rounded-2xl border transition-all duration-200 text-left',
                action.enabled
                  ? `${action.bgColor} cursor-pointer active:scale-[0.98]`
                  : 'bg-white/[0.02] border-white/[0.04] cursor-not-allowed opacity-40'
              )}
            >
              {/* Glow on hover */}
              {action.enabled && (
                <div
                  className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
                  style={{ boxShadow: `inset 0 0 30px ${action.glowColor}10, 0 0 40px ${action.glowColor}08` }}
                />
              )}
              <div className={cn(
                'w-12 h-12 rounded-xl flex items-center justify-center',
                action.enabled ? `bg-white/[0.06]` : 'bg-white/[0.03]'
              )}>
                <action.icon className={cn('w-6 h-6', action.enabled ? action.color : 'text-white/20')} />
              </div>
              <div>
                <h3 className={cn('text-sm font-bold mb-1', action.enabled ? 'text-white' : 'text-white/30')}>
                  {action.label}
                </h3>
                <p className={cn('text-xs leading-relaxed', action.enabled ? 'text-white/40' : 'text-white/15')}>
                  {action.description}
                </p>
              </div>
              {!action.enabled && (
                <span className="absolute top-4 right-4 text-[9px] font-semibold uppercase text-white/20 bg-white/[0.04] px-2 py-0.5 rounded">
                  Restricted
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Footer */}
      <footer className="relative z-10 text-center py-3 border-t"
        style={{ borderColor: 'rgba(255,255,255,0.04)' }}
      >
        <p className="text-[10px] text-white/20 font-mono">C2 Command &amp; Control System v1.0.0</p>
      </footer>

      {/* Create Event Modal */}
      <CreateEventModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={handleEventCreated}
      />

      {/* View Events Modal */}
      <ViewEventsModal
        open={showEvents}
        onClose={() => setShowEvents(false)}
        onSelect={handleEventSelected}
      />

      {/* Emergency Alert */}
      <EmergencyAlert />
    </div>
  );
}
